import { ExternalLinkIcon } from '@heroicons/react/solid';
import clsx from 'clsx';
import { formatInTimeZone } from 'date-fns-tz';
import { LayoutGroup, motion } from 'framer-motion';
import get from 'lodash/get';
import dynamic from 'next/dynamic';
import party from 'party-js';
import React, { useEffect, useRef, useState } from 'react';
import { useAlgo } from '../hooks/useAlgo';
import useNfd from '../hooks/useNfd';
import useRaffleOwner from '../hooks/useRaffleOwner';
import { useRaffles } from '../hooks/useRaffles';
import useTokenBalance from '../hooks/useTokenBalance';
import formatNumber from '../utils/formatNumber';
import Alert from './Alert';
import Button from './Button';
import ConnectWalletMessage from './ConnectWalletMessage';
import Countdown from './Countdown';
import RowEntry from './RowEntry';
import RowTransaction from './RowTransaction';
import Spinner from './Spinner';

export function Detail({ label, value, children, success }) {
  return (
    <p className='flex flex-col'>
      <span className='font-bold text-text opacity-60'>{label}</span>{' '}
      <span className={clsx(' text-xl font-bold', success ? 'text-green-500' : 'text-text')}>{children || value}</span>
    </p>
  );
}

function Raffle({ raffle }) {
  const { id, assetId, endDate, owner } = raffle;
  const raffleOwner = useRaffleOwner(owner);
  const { address, sendTokensToTokenWalletAndOptIn } = useAlgo();
  const { joinRaffle, getEntries, setBalance, balance } = useRaffles();
  const [numberOfTickets, setNumberOfTickets] = useState(0);
  const [entries, setEntries] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [setEntriesLoading, setSetEntriesLoading] = useState(false);
  const { getNfd } = useNfd();
  const [winner, setWinner] = useState(null);
  const [tabs, setTabs] = useState(['Details', 'Entries', 'Transactions']);
  const [activeTab, setActiveTab] = useState('Details');
  const tokenBalance = useTokenBalance(raffleOwner.token);

  const formattedEntries = entries
    ?.map((entry) => {
      return {
        sender: entry.transaction.sender,
        amount: entry.tickets,
      };
    })
    .reduce((acc, curr) => {
      const { sender, amount } = curr;
      if (!acc[sender]) {
        acc[sender] = 0;
      }
      acc[sender] += amount;
      return acc;
    }, {});

  // sort formattedEntries by value
  const sortedEntries = Object.keys(formattedEntries)
    .map((key) => {
      return {
        address: key,
        amount: formattedEntries[key],
      };
    })
    .sort((a, b) => b.amount - a.amount);

  const confettiRef = useRef(null);

  const numberOfEntries = sortedEntries.reduce((acc, curr) => {
    return acc + curr.amount;
  }, 0);

  const numberOfYourEntries = sortedEntries.reduce((acc, curr) => {
    return acc + (curr.address === address ? curr.amount : 0);
  }, 0);

  const odds = numberOfEntries > 0 ? `${Math.round((numberOfYourEntries / numberOfEntries) * 100)}%` : '0%';

  // check that date is not in the past
  const now = new Date();
  const raffleDate = new Date(endDate);
  let raffleIsOver = raffleDate < now;

  const enterRaffle = async () => {
    setSuccess(false);

    if (!numberOfTickets || numberOfTickets === 0) {
      setError('You must buy at least 1 ticket');
      return;
    }

    if (raffle.maxEntries && numberOfTickets + numberOfYourEntries > raffle.maxEntries) {
      setError(`You cannot buy more than ${raffle.maxEntries} tickets`);
      return;
    }

    if (raffle.totalTickets && numberOfEntries === raffle.totalTickets) {
      setError(`All tickets have been purchased`);
      return;
    }

    if (raffle.totalTickets && numberOfEntries + numberOfTickets > raffle.totalTickets) {
      setError(`You cannot buy that many tickets`);
      return;
    }

    try {
      setError(null);
      setLoading(true);
      const signedTxns = await sendTokensToTokenWalletAndOptIn(
        raffleOwner.token,
        assetId,
        numberOfTickets * raffle.ticketCost,
        raffleOwner.divisible
      );
      await joinRaffle(signedTxns, id, address, numberOfTickets);

      const them = await getEntries(id);
      setEntries(them);

      setBalance(balance - numberOfTickets * raffle.ticketCost);
      setSuccess(numberOfTickets);
      setNumberOfTickets(0);

      party.confetti(confettiRef.current, {
        count: party.variation.range(20, 60),
      });
      party.confetti(confettiRef.current, {
        count: party.variation.range(20, 60),
      });
      party.confetti(confettiRef.current, {
        count: party.variation.range(20, 60),
      });
    } catch (error) {
      console.error(error);
      setError(error?.response?.data?.error || error?.response?.data || error?.message || 'Unexpected error occurred');
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  const addMax = () => {
    const max = Math.floor(tokenBalance || balance / raffle.ticketCost);
    let trueMax = max > raffle.maxEntries ? raffle.maxEntries : max;
    if (numberOfYourEntries > 0 && numberOfYourEntries < raffle.maxEntries) {
      trueMax = raffle.maxEntries - numberOfYourEntries;
    }
    setNumberOfTickets(trueMax);
  };

  useEffect(() => {
    const event = 'beforeunload';
    const prevent = (event) => {
      if (loading) {
        event.preventDefault();
        event.returnValue = 'Tickets are being purchased. If you leave this page, you will lose your entries.';
      }
    };

    window.addEventListener(event, prevent);

    return () => {
      window.removeEventListener(event, prevent);
    };
  }, [loading]);

  useEffect(() => {
    if (!raffle.winner) return;

    (async () => {
      try {
        const nfd = await getNfd(raffle.winner);
        if (nfd) {
          return setWinner(get(nfd, 'name'));
        }
        setWinner(`${raffle.winner?.slice(0, 4)}...${raffle.winner?.slice(-4)}`);
      } catch (error) {
        console.error(error);
      }
    })();
  }, [raffle.winner]);

  useEffect(() => {
    (async function () {
      try {
        setSetEntriesLoading(true);
        const them = await getEntries(id);
        setEntries(them);
      } catch (error) {
        console.error(error);
      } finally {
        setSetEntriesLoading(false);
      }
    })();
  }, [address]);

  return (
    <LayoutGroup>
      <div className='grid w-full grid-cols-12 gap-y-10 md:gap-10'>
        <div className='col-span-12 md:col-span-6 lg:col-span-5'>
          <div className='relative flex items-center justify-center w-full overflow-hidden rounded-lg aspect-square bg-neutral-900'>
            <motion.img src={raffle.image} alt={raffle.name} className='absolute inset-0 z-10 object-cover w-full h-full rounded-lg' />
            <Spinner dimensionClasses='w-8 h-8' />
          </div>

          <div className='mt-5'>
            {address ? (
              !raffleIsOver && (
                <div className='space-y-5'>
                  <div className='relative flex flex-col justify-start space-y-1'>
                    <div className='flex justify-between'>
                      <label htmlFor='numberOfTickets' className='mb-1 text-lg font-medium text-neutral-300'>
                        No. of tickets
                      </label>
                      <button type='button' className='underline' onClick={addMax}>
                        Max
                      </button>
                    </div>

                    <input
                      type='number'
                      name='numberOfTickets'
                      min={0}
                      value={numberOfTickets}
                      onChange={(e) => setNumberOfTickets(parseInt(e.target.value))}
                      className='block w-full mt-1 border-transparent rounded-md shadow-sm bg-neutral-900 text-neutral-200 focus:border-primary-500 focus:ring focus:ring-primary-500 focus:ring-opacity-50'
                    />
                  </div>

                  {error && (
                    <div className='mb-1'>
                      <Alert type='danger'>🚨 {error}</Alert>
                    </div>
                  )}

                  {success && (
                    <div className='mb-1'>
                      <Alert>🥳 {success} tickets have been entered into the raffle!</Alert>
                    </div>
                  )}

                  <div ref={confettiRef}></div>
                  {raffle.maxEntries ? (
                    <Button
                      className='w-full !flex text-center justify-center'
                      disabled={numberOfYourEntries >= raffle.maxEntries}
                      onClick={enterRaffle}
                    >
                      {loading ? (
                        <Spinner dimensionClasses='w-4 h-4' />
                      ) : (
                        <span>
                          {numberOfYourEntries >= raffle.maxEntries
                            ? `You've purchased ${raffle.maxEntries}/${raffle.maxEntries} tickets!`
                            : 'Enter Raffle'}
                        </span>
                      )}
                    </Button>
                  ) : (
                    <Button className='w-full !flex justify-center' onClick={enterRaffle}>
                      {loading ? <Spinner dimensionClasses='w-4 h-4' /> : <span>Enter Raffle</span>}
                    </Button>
                  )}
                </div>
              )
            ) : (
              <div className='flex justify-center w-full'>
                <ConnectWalletMessage />
              </div>
            )}
          </div>
        </div>
        <div className='relative col-span-12 md:col-span-6 lg:col-span-7'>
          <div className='relative py-6 rounded-lg md:px-10 md:bg-neutral-900 text-neutral-200'>
            <div>
              <div>
                <h2 className='mb-1 font-bold text-text opacity-60'>{raffle.collection}</h2>
                <h3 className='flex items-center mb-1 space-x-3 text-2xl font-bold'>
                  <span>{raffle.name}</span>
                  <span>
                    <a href={`https://www.downbad.farm/asset/${raffle.assetId}`} target='_blank' rel='noreferrer'>
                      <div className='w-6 opacity-50 text-text'>
                        <ExternalLinkIcon />
                      </div>
                    </a>
                  </span>
                </h3>
              </div>

              <div className='mb-5 md:mb-10'>
                <div className='w-full'>
                  <div className='border-b border-gray-200'>
                    <nav className='flex -mb-px' aria-label='Tabs'>
                      {tabs.map((tab) => (
                        <div key={tab} className='relative flex flex-col w-1/3'>
                          <button
                            className={clsx(
                              tab === activeTab ? 'text-primary-500' : 'text-text',
                              'py-4 px-1 text-center border-b-2 font-medium border-transparent transition duration-200 hover:opacity-75'
                            )}
                            onClick={() => setActiveTab(tab)}
                          >
                            {tab}
                          </button>
                          {tab === activeTab && (
                            <motion.div layoutId='tab-underline' className='absolute bottom-0 left-0 right-0 w-full h-[2px] bg-primary-500' />
                          )}
                        </div>
                      ))}
                    </nav>
                  </div>
                </div>
              </div>

              {activeTab === 'Details' && (
                <div className='grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-x-10 md:gap-y-10'>
                  <p className='flex flex-col'>
                    <span className='font-bold text-text opacity-60'>Hosted By</span>{' '}
                    <a href={raffleOwner.link} s className={clsx('text-xl font-bold text-text')} style={{ color: raffleOwner.color }}>
                      {raffleOwner.collection}
                    </a>
                  </p>
                  <div></div>
                  <Detail label='Ticket cost' value={`${raffle.ticketCost} ${raffleOwner.tokenAbbr}`} />
                  {address ? <Detail label={`${raffleOwner.tokenAbbr} Balance`} value={formatNumber(tokenBalance)} /> : <div></div>}

                  {raffle.startDate && (
                    <Detail label='Start date' value={`${formatInTimeZone(raffle.startDate, 'America/New_York', 'MMM dd, yyyy h:mm aaa')}`} />
                  )}
                  {raffle.endDate && (
                    <Detail label='End date' value={`${formatInTimeZone(raffle.endDate, 'America/New_York', 'MMM dd, yyyy h:mm aaa')}`} />
                  )}

                  <Detail label='Raffle ends in'>
                    {new Date(raffle.endDate) < new Date() ? <div>Raffle has ended</div> : <Countdown date={raffle.endDate} simple />}
                  </Detail>

                  <Detail
                    label='Total Entries'
                    value={
                      raffle.totalTickets
                        ? `${formatNumber(numberOfEntries) || 0}/${formatNumber(raffle.totalTickets)}`
                        : formatNumber(numberOfEntries) || 0
                    }
                  />
                  <Detail label={`Total ${raffleOwner.tokenAbbr} Spent`} value={formatNumber(numberOfEntries * raffle.ticketCost) || 0} />
                  {address && (
                    <Detail
                      label='My Entries'
                      value={raffle.maxEntries ? `${numberOfYourEntries || 0}/${raffle.maxEntries}` : numberOfYourEntries || 0}
                    />
                  )}
                  <Detail label={`My ${raffleOwner.tokenAbbr} Spent`} value={formatNumber(numberOfYourEntries * raffle.ticketCost) || 0} />
                  {address && <Detail label='My Odds' value={odds} />}

                  {raffle.winner && <Detail label='Winner' value={winner} success />}
                </div>
              )}

              {activeTab === 'Entries' && (
                <div className='overflow-scroll'>
                  <table className='min-w-full divide-y divide-gray-800'>
                    <thead>
                      <tr>
                        <th scope='col' className='px-3 pb-2 font-bold text-left text-text'>
                          Wallet
                        </th>
                        <th scope='col' className='pb-2 pr-8 font-bold text-center text-text'>
                          Tickets
                        </th>
                        <th scope='col' className='pb-2 pr-8 font-bold text-center text-text'>
                          Odds
                        </th>
                        <th scope='col' className='pb-2 pr-8 font-bold text-center text-text'>
                          Cost
                        </th>
                      </tr>
                    </thead>

                    <tbody className='divide-y divide-gray-800'>
                      {sortedEntries.map((entry, index) => (
                        <RowEntry key={index} entry={entry} raffle={raffle} numberOfEntries={numberOfEntries} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {activeTab === 'Transactions' && (
                <div className='overflow-scroll'>
                  <table className='min-w-full divide-y divide-gray-800'>
                    <thead>
                      <tr>
                        <th scope='col' className='px-3 pb-2 font-bold text-left text-text'>
                          Txn
                        </th>
                        <th scope='col' className='px-3 pb-2 font-bold text-left text-text'>
                          Buyer
                        </th>
                        <th scope='col' className='px-3 pb-2 font-bold text-left text-text'>
                          Date
                        </th>
                        <th scope='col' className='pb-2 pr-8 font-bold text-center text-text'>
                          Tickets
                        </th>
                        <th scope='col' className='pb-2 pr-8 font-bold text-center text-text'>
                          Cost
                        </th>
                      </tr>
                    </thead>

                    <tbody className='divide-y divide-gray-800'>
                      {entries.map((entry, index) => (
                        <RowTransaction key={index} entry={entry} raffle={raffle} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </LayoutGroup>
  );
}

export default Raffle;
